import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Crown, CalendarClock, RefreshCw, CreditCard, AlertCircle, CheckCircle2, X, Bell, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
};

const Account = () => {
  const navigate = useNavigate();
  const { user, login, logout, premium, refreshPremium } = useAuth();
  const [payments, setPayments] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    refreshPremium();
    try {
      const r = await api.get("/premium/payments");
      setPayments(r.data || []);
    } catch {
      /* ignore */
    }
  }, [user, refreshPremium]);

  useEffect(() => {
    load();
  }, [load]);

  if (!user) {
    return (
      <div data-testid="account-page" className="flex flex-col items-center justify-center py-32 text-center">
        <h2 className="font-display text-3xl mb-2">Sign in to view your account</h2>
        <button onClick={login} className="mt-4 bg-white text-black rounded-full px-6 py-2.5 font-semibold">Sign in</button>
      </div>
    );
  }

  const tier = premium?.tier || "free";
  const active = premium?.active;
  const cancelAtEnd = premium?.cancel_at_period_end;

  const doCancel = async () => {
    setBusy(true);
    try {
      await api.post("/premium/cancel");
      refreshPremium();
      toast.success("Auto-renew disabled. You'll keep Premium until your expiry date.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setBusy(false);
    }
  };

  const doResume = async () => {
    setBusy(true);
    try {
      await api.post("/premium/resume");
      refreshPremium();
      toast.success("Auto-renew re-enabled.");
    } catch {
      toast.error("Resume failed");
    } finally {
      setBusy(false);
    }
  };

  const subscribe = async () => {
    setBusy(true);
    try {
      const r = await api.post("/premium/checkout", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch {
      toast.error("Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div data-testid="account-page" className="pb-16 max-w-4xl">
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-3">Account</p>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter">Manage Subscription</h1>
      </div>

      {/* Profile */}
      <div className="flex items-center gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/10 mb-6">
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full object-cover" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-xl">
            {user.name?.[0]?.toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{user.name}</p>
          <p className="text-sm text-white/50 truncate">{user.email}</p>
        </div>
        <button
          onClick={logout}
          data-testid="account-logout-btn"
          className="text-sm text-white/60 hover:text-white px-3 py-1.5 rounded-full hover:bg-white/5 transition"
        >
          Sign out
        </button>
      </div>

      {/* Notifications shortcut */}
      <Link
        to="/notifications"
        data-testid="account-notifications-link"
        className="flex items-center gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/8 hover:border-white/20 hover:bg-white/[0.05] transition mb-3 group"
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF2E00]/30 to-[#FF2E00]/20 flex items-center justify-center">
          <Bell size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Release alerts</p>
          <p className="text-xs text-white/50 truncate">Email + push when your tracked artists drop new music</p>
        </div>
        <ChevronRight size={18} className="text-white/40 group-hover:text-white transition" />
      </Link>

      {/* Settings shortcut */}
      <Link
        to="/settings"
        data-testid="account-settings-link"
        className="flex items-center gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/8 hover:border-white/20 hover:bg-white/[0.05] transition mb-6 group"
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E2FF31]/25 to-[#FF2E00]/15 flex items-center justify-center">
          <CreditCard size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Settings &amp; payment methods</p>
          <p className="text-xs text-white/50 truncate">Cards, billing portal, help center, sign out</p>
        </div>
        <ChevronRight size={18} className="text-white/40 group-hover:text-white transition" />
      </Link>

      {/* Subscription card */}
      <div className="p-6 rounded-2xl bg-gradient-to-br from-white/[0.04] to-white/[0.02] border border-white/10 mb-6">
        <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${active ? "bg-gradient-to-br from-[#FF2E00] to-[#E2FF31]" : "bg-white/10"}`}>
              <Crown size={20} className={active ? "text-black" : "text-white/40"} />
            </div>
            <div>
              <p className="font-display text-2xl font-semibold capitalize">
                {tier === "trial" ? "Free Trial" : tier === "premium" ? "Premium" : "Free Plan"}
              </p>
              <p className="text-sm text-white/50">
                {active
                  ? `${premium.days_left} day${premium.days_left === 1 ? "" : "s"} remaining`
                  : "No active subscription"}
              </p>
            </div>
          </div>
          {active && (
            <span
              data-testid="autorenew-pill"
              className={`text-xs px-3 py-1.5 rounded-full ${
                cancelAtEnd
                  ? "bg-white/10 text-white/60"
                  : "bg-[#E2FF31]/15 text-[#E2FF31] border border-[#E2FF31]/30"
              }`}
            >
              {cancelAtEnd ? "Auto-renew off" : "Auto-renew on"}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <div className="p-4 rounded-xl bg-black/30 border border-white/5">
            <div className="flex items-center gap-2 text-xs text-white/50 mb-2">
              <CalendarClock size={14} /> {cancelAtEnd ? "Expires" : "Next charge"}
            </div>
            <p className="text-sm font-medium" data-testid="next-charge-date">{formatDate(premium?.expires_at)}</p>
          </div>
          <div className="p-4 rounded-xl bg-black/30 border border-white/5">
            <div className="flex items-center gap-2 text-xs text-white/50 mb-2">
              <CreditCard size={14} /> Amount
            </div>
            <p className="text-sm font-medium">${premium?.price_usd?.toFixed(2)} / month</p>
          </div>
          <div className="p-4 rounded-xl bg-black/30 border border-white/5">
            <div className="flex items-center gap-2 text-xs text-white/50 mb-2">
              <RefreshCw size={14} /> Billing cycle
            </div>
            <p className="text-sm font-medium">Monthly</p>
          </div>
        </div>

        {cancelAtEnd && active && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FF2E00]/10 border border-[#FF2E00]/20 mb-4">
            <AlertCircle size={18} className="text-[#FF2E00] mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-sm">
              <p className="font-medium">Subscription cancelled</p>
              <p className="text-white/60 mt-0.5">
                You'll keep Premium access until {formatDate(premium?.expires_at)}. Resume any time before then.
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          {!active && (
            <button
              data-testid="account-subscribe-btn"
              onClick={subscribe}
              disabled={busy}
              className="bg-[#FF2E00] text-white font-semibold rounded-full px-6 py-2.5 hover:bg-[#E62900] hover:shadow-[0_0_20px_rgba(255,51,102,0.4)] transition disabled:opacity-50"
            >
              Subscribe — $10/month
            </button>
          )}
          {active && cancelAtEnd && (
            <button
              data-testid="account-resume-btn"
              onClick={doResume}
              disabled={busy}
              className="bg-[#E2FF31] text-black font-semibold rounded-full px-6 py-2.5 hover:opacity-90 transition disabled:opacity-50"
            >
              Resume auto-renew
            </button>
          )}
          {active && !cancelAtEnd && (
            <>
              <button
                data-testid="account-renew-btn"
                onClick={subscribe}
                disabled={busy}
                className="bg-white text-black font-semibold rounded-full px-6 py-2.5 hover:scale-[1.02] transition disabled:opacity-50"
              >
                Renew now (+30 days)
              </button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    data-testid="account-cancel-btn"
                    disabled={busy}
                    className="bg-white/5 hover:bg-white/15 text-white/80 rounded-full px-6 py-2.5 transition disabled:opacity-50"
                  >
                    Cancel subscription
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#0a0a0a] border-white/10 text-white">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                    <AlertDialogDescription className="text-white/60">
                      Your Premium access will remain active until {formatDate(premium?.expires_at)}.
                      After that, you'll lose access to unlimited playlists, ad-free listening, and other perks.
                      You can resume any time.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-white/5 hover:bg-white/15 border-white/10 text-white">Keep Premium</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={doCancel}
                      data-testid="account-confirm-cancel-btn"
                      className="bg-[#FF2E00] hover:bg-[#E62900] text-white"
                    >
                      Yes, cancel
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>

      {/* Payment history */}
      <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/10">
        <h3 className="font-display text-xl mb-4">Payment history</h3>
        {payments.length === 0 ? (
          <p className="text-sm text-white/40">No payments yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-white/40 border-b border-white/5">
                  <th className="text-left pb-3 font-medium">Date</th>
                  <th className="text-left pb-3 font-medium">Amount</th>
                  <th className="text-left pb-3 font-medium">Status</th>
                  <th className="text-left pb-3 font-medium">Session</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {payments.map((p) => (
                  <tr key={p.session_id} data-testid={`payment-${p.session_id}`}>
                    <td className="py-3 text-white/80">{formatDate(p.created_at)}</td>
                    <td className="py-3">${(p.amount || 0).toFixed(2)} {(p.currency || "usd").toUpperCase()}</td>
                    <td className="py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${
                          p.payment_status === "paid"
                            ? "bg-[#E2FF31]/15 text-[#E2FF31]"
                            : p.payment_status === "unpaid"
                            ? "bg-white/5 text-white/50"
                            : "bg-[#FF2E00]/15 text-[#FF2E00]"
                        }`}
                      >
                        {p.payment_status === "paid" ? <CheckCircle2 size={12} /> : <X size={12} />}
                        {p.payment_status}
                      </span>
                    </td>
                    <td className="py-3 font-mono text-xs text-white/40">{p.session_id?.slice(0, 18)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <button onClick={() => navigate("/")} className="text-sm text-white/50 hover:text-white mt-6">
        ← Back to home
      </button>
    </div>
  );
};

export default Account;
